"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { voiceInput, captureScreenshot, captureScreenshotV2 } from "@/lib/api";
import type { CaptureResponse, CaptureRouteState } from "@/lib/api";

interface Props {
  onSubmit: (text: string) => Promise<void>;
  // Modern screenshot bridge — receives the fully-routed capture
  // state from /captures/screenshot in one round-trip, so the parent
  // can render the popup without re-running create+classify+route on
  // the OCR'd text (no orphan CaptureItem, no orphan Thought row).
  onScreenshotRouted?: (text: string, routeState: CaptureRouteState) => Promise<void>;
  // Legacy screenshot handler — kept for backward compat with callers
  // that haven't migrated to onScreenshotRouted yet.
  onScreenshotCapture?: (response: CaptureResponse) => void;
  // Surface failures to the user (mic permission, empty transcript,
  // OCR returned no text, network). Without this, the catches just
  // swallowed errors and the user clicked the button to no effect.
  onError?: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  focusSignal?: number;
}

export default function CaptureInput({
  onSubmit,
  onScreenshotRouted,
  onScreenshotCapture,
  onError,
  isLoading = false,
  placeholder = "Capture a thought, link, or idea...",
  focusSignal = 0,
}: Props) {
  const [text, setText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (file.size > 8 * 1024 * 1024) {
      onError?.("Screenshot too large — max 8 MB.");
      return;
    }
    setIsUploading(true);
    try {
      // Prefer the V2 atomic endpoint when the parent has opted in —
      // OCR + classify + route in one round-trip, no orphan rows.
      // Falls back to the legacy /thoughts/screenshot bridge for any
      // caller still on the old contract.
      if (onScreenshotRouted) {
        const routeState = await captureScreenshotV2(file);
        const text = routeState.capture.raw_text?.trim() || "";
        if (text) {
          await onScreenshotRouted(text, routeState);
        } else {
          onError?.("Couldn't read any text from that screenshot.");
        }
      } else {
        const result = await captureScreenshot(file);
        const ocrText = result.thought?.raw_text?.trim() || "";
        if (ocrText) {
          await onSubmit(ocrText);
        } else if (onScreenshotCapture) {
          onScreenshotCapture(result);
        } else {
          onError?.("Couldn't read any text from that screenshot.");
        }
      }
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Screenshot capture failed.";
      onError?.(message);
    } finally {
      setIsUploading(false);
    }
  }, [onError, onScreenshotCapture, onScreenshotRouted, onSubmit]);

  const handleScreenshotClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, [focusSignal]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    setText("");
    void onSubmit(trimmed);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          onError?.("Didn't catch any audio — try again.");
          return;
        }

        setIsTranscribing(true);
        try {
          const result = await voiceInput(blob, "recording.webm");
          const transcript = result.transcript?.trim() || "";
          if (transcript) {
            await onSubmit(transcript);
          } else {
            // Backend returned a 200 but no transcript — most often
            // a too-short / silent recording. Surface this so the user
            // knows the click registered but produced nothing.
            onError?.("Couldn't hear anything to transcribe.");
          }
        } catch (err) {
          const message =
            err instanceof Error && err.message
              ? err.message
              : "Voice transcription failed.";
          onError?.(message);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      const message =
        err instanceof Error && err.name === "NotAllowedError"
          ? "Mic permission denied — enable it in your browser settings."
          : err instanceof Error && err.message
            ? err.message
            : "Mic not available on this device.";
      onError?.(message);
    }
  }, [onError, onSubmit]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const handleVoiceClick = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const busy = isLoading || isTranscribing || isUploading;

  return (
    <div
      className={`
        flex items-center gap-2
        w-full px-5 py-3
        bg-card rounded-full
        border transition-colors duration-200
        ${isRecording ? "border-rose" : isFocused ? "border-primary" : "border-border"}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={inputRef}
        type="text"
        value={
          isRecording ? "Recording..." :
          isTranscribing ? "Transcribing..." :
          isUploading ? "Reading screenshot..." :
          text
        }
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        // Stable accessible name even after the placeholder dynamically
        // changes ("Recording..." / "Transcribing..." / "Reading
        // screenshot..."). Without this, screen readers lose track of
        // what the field is for.
        aria-label="Capture"
        disabled={busy || isRecording}
        className="flex-1 bg-transparent border-0 outline-none text-text placeholder:text-muted text-[0.88rem]"
      />

      {busy ? (
        <span className="flex-shrink-0 w-5 h-5 border-2 border-muted/30 border-t-primary rounded-full animate-spin" />
      ) : text.trim() && !isRecording ? (
        <button
          onClick={handleSubmit}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary text-white hover:opacity-90 transition-opacity"
          aria-label="Send"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      ) : (
        <>
          {/* Screenshot button */}
          {onScreenshotCapture && !isRecording && (
            <button
              onClick={handleScreenshotClick}
              disabled={busy}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-muted hover:text-primary hover:bg-secondary/50 transition-colors disabled:opacity-40"
              aria-label="Capture screenshot"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
          )}
          {/* Voice button */}
          <button
            onClick={handleVoiceClick}
            className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
              isRecording
                ? "bg-rose text-white animate-pulse"
                : "text-muted hover:text-primary hover:bg-secondary/50"
            }`}
            aria-label={isRecording ? "Stop recording" : "Voice input"}
          >
            {isRecording ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>
        </>
      )}
    </div>
  );
}
